import {groupByUniqueKey} from '@benchling/js-utils';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineEdgeUpsert, UpdateStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineEdge, PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {getPossibleEdges} from 'pipelines/StepConnectionUtils';

/**
 * For some step types with multiple input step datas, child steps should only be reparented based on one
 * parent. If a step type is included here, only one input step data of the deleted step is traversed.
 */
const PRIMARY_INPUT_STEP_DATAS_TO_TRAVERSE: Partial<Record<PipelineStepType, string>> = {
  [PipelineStepType.REGRESSION_INTERPOLATION]: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
};

/**
 * Get the incoming edges to traverse when find an ancestor step to reconnect to.
 *
 * By default all incoming edges are traversed; however, for step types in
 * `PRIMARY_INPUT_STEP_DATAS_TO_TRAVERSE`, only one input step data is traversed.
 */
function getIncomingEdgesToTraverse(step: PipelineStep): Array<PipelineEdge> {
  const primaryInputStepDataName = PRIMARY_INPUT_STEP_DATAS_TO_TRAVERSE[step.stepType];
  if (primaryInputStepDataName != null) {
    return step.incomingEdges.filter((edge) => edge.toStepData.name === primaryInputStepDataName);
  }
  return step.incomingEdges;
}

/**
 * Find the nearest ancestor steps not being deleted.
 *
 * @param step A step to delete
 * @param stepIdToStep Map of pipeline step IDs to steps
 * @param stepsToDeleteIds Step IDs of the steps to delete
 * @returns An array of ancestor steps which are not being deleted.
 */
function findNearestAncestorsToNotDelete(
  step: PipelineStep,
  stepIdToStep: Record<string, PipelineStep>,
  stepsToDeleteIds: Set<string>
): Array<PipelineStep> {
  const ancestorStepIds = new Set<string>();
  const queue: Array<PipelineStep> = [step];
  const visited = new Set<string>();
  while (queue.length > 0) {
    const currentStep = queue.shift();
    if (currentStep == null || visited.has(currentStep.id)) {
      continue;
    }
    visited.add(currentStep.id);
    if (stepsToDeleteIds.has(currentStep.id)) {
      getIncomingEdgesToTraverse(currentStep).forEach((edge) => queue.push(stepIdToStep[edge.fromStep.id]));
    } else {
      ancestorStepIds.add(currentStep.id);
    }
  }
  return [...ancestorStepIds].map((stepId) => stepIdToStep[stepId]);
}

/**
 * For a step whose parent is being deleted, create a new incoming edges to "reparent" the edge.
 *
 * An edge will re-connected if there is exactly one ancestor step to which the edge can be re-connected.
 *
 * @param step A step whose parent is being deleted
 * @param edge The incoming edge to re-connect
 * @param stepIdToStep Map of pipeline step IDs to steps
 * @param stepsToDeleteIds Step IDs of the steps to delete
 * @returns A `PipelineEdgeUpsert` for the re-connected edge, or null if ambiguous or impossible.
 */
function reconnectEdge(
  step: PipelineStep,
  edge: PipelineEdge,
  stepIdToStep: Record<string, PipelineStep>,
  stepsToDeleteIds: Set<string>
): PipelineEdgeUpsert | null {
  // Find nearest ancestor steps that are not being deleted; these are candidates to reparent to.
  const ancestorSteps = findNearestAncestorsToNotDelete(
    stepIdToStep[edge.fromStep.id],
    stepIdToStep,
    stepsToDeleteIds
  );

  // Check what connections are possible between `edge.toStepData` and the candidate parents.
  const possibleConnections = ancestorSteps
    .map((ancestorStep) => ({
      ancestorStep,
      possibleEdges: getPossibleEdges({
        fromStepType: ancestorStep.stepType,
        toStepType: step.stepType,
        toStepData: {systemName: edge.toStepData.name},
      }),
    }))
    .filter(({possibleEdges}) => possibleEdges.length > 0);

  // If exactly 1 connection is possible, return it.
  if (possibleConnections.length === 1 && possibleConnections[0].possibleEdges.length === 1) {
    const {
      ancestorStep,
      possibleEdges: [{fromStepData, toStepData}],
    } = possibleConnections[0];
    return {
      fromStepId: ancestorStep.id,
      fromStepDataName: fromStepData.systemName,
      fromStepDataIndex: !fromStepData.isMulti || !toStepData.isMulti ? 0 : null,
      toStepDataName: edge.toStepData.name,
      toStepDataIndex: edge.toStepDataIndex,
      condition: edge.condition,
    };
  }

  // There are either no or multiple ways to re-connect the step.
  return null;
}

/**
 * For a step whose parent is being deleted, create new incoming edges to "reparent" the step.
 *
 * An edge will re-connected if there is exactly one ancestor step to which the edge can be re-connected.
 *
 * @param step The step whose parent is being deleted
 * @param stepIdToStep Map of pipeline step IDs to steps
 * @param stepsToDeleteIds Step IDs of the steps to delete
 * @returns An array of `PipelineEdgeUpsert` which should replace the current incoming edges of `step`.
 */
function reconnectStep(
  step: PipelineStep,
  stepIdToStep: Record<string, PipelineStep>,
  stepsToDeleteIds: Set<string>
): Array<PipelineEdgeUpsert> {
  const newEdges = step.incomingEdges.map((edge) => ({
    oldEdge: edge,
    newEdge: stepsToDeleteIds.has(edge.fromStep.id)
      ? // Attempt to reconnect the edge, if the parent step is being deleted.
        reconnectEdge(step, edge, stepIdToStep, stepsToDeleteIds)
      : // Maintain the existing edge, without modifications, iif the parent step is not being deleted.
        {
          fromStepId: edge.fromStep.id,
          fromStepDataName: edge.fromStepData.name,
          fromStepDataIndex: edge.fromStepDataIndex,
          toStepDataName: edge.toStepData.name,
          toStepDataIndex: edge.toStepDataIndex,
          condition: edge.condition,
        },
  }));

  // If an edge with an explicit `toStepDataIndex` cannot be reconnected, then adjust the remaining edges'
  // `toStepDataIndex` values, to remain sequential.
  const toStepDatasWithLostIndex = new Set(
    newEdges
      .filter(({oldEdge, newEdge}) => newEdge == null && oldEdge.toStepDataIndex != null)
      .map(({oldEdge}) => oldEdge.toStepData.name)
  );
  const adjustedToStepDataIndex = Object.fromEntries(
    [...toStepDatasWithLostIndex].map((toStepDataName) => [toStepDataName, 0])
  );
  const adjustedEdges = newEdges
    .map(({newEdge}) => newEdge)
    .filter((newEdge): newEdge is PipelineEdgeUpsert => newEdge != null)
    .map((newEdge) =>
      toStepDatasWithLostIndex.has(newEdge.toStepDataName)
        ? {...newEdge, toStepDataIndex: adjustedToStepDataIndex[newEdge.toStepDataName]++}
        : newEdge
    );

  return adjustedEdges;
}

/**
 * Delete steps from a pipeline, and infer edges to re-connect remaining steps.
 *
 * This function tries to make reasonable inferences on how to reconnect edges.
 *
 * In particular, a remaining child step is reconnected if there is a single valid way to reconnect it. This
 * may leave a remaining step disconnected, if there are either no or multiple options to reconnect it.
 *
 * Steps becoming disconnected is considered acceptable, since users can manually reconnect steps from the
 * flowchart.
 *
 * The alogorithm for reconnecting steps may be adjusted in the future, if it proves unintuitive.
 */
export async function deleteStepsWithInferredEdges(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>,
  stepIds: Array<string>
): Promise<void> {
  const stepIdToStep = groupByUniqueKey(pipeline.steps, (step) => step.id);
  const stepsToDeleteIds = new Set(stepIds);
  const stepsToDelete = pipeline.steps.filter((step) => stepsToDeleteIds.has(step.id));

  // Find children of steps to delete, which are not themselves being deleted.
  const childStepIds = new Set(
    stepsToDelete.flatMap((step) =>
      step.outgoingEdges.map((edge) => edge.toStep.id).filter((toStepId) => !stepsToDeleteIds.has(toStepId))
    )
  );
  const childSteps = pipeline.steps.filter((step) => childStepIds.has(step.id));

  // Update child steps to remove or reconnect incoming edges
  const stepsToUpdate: Array<UpdateStepParams> = childSteps.map((step) => ({
    stepId: step.id,
    incomingEdges: reconnectStep(step, stepIdToStep, stepsToDeleteIds),
    rerunAfterUpdate: true, // Attempt to rerun child steps after updating their inputs.
  }));

  await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: [],
    stepsToUpdate,
    stepsToDelete: stepIds.map((stepId) => ({stepId})),
  });
}
